import React, { FC, useContext } from 'react';
import Tooltip from 'react-tooltip';
import { FormattedMessage, injectIntl } from 'react-intl';
import { snakeToCamel } from '../../../caseUtils';
import {
  payingChoice,
  yesNoChoice,
  getFeesInfo,
  acceptedChoice,
  paymentMeansToString,
} from '../../../payment/paymentUtils';
import {
  EntityCardContainer,
  EntityCardTitle,
  EntityCardMaterialIcon,
} from '../../../entity/components/EntityCardComponents';
import { MaterialIcon } from '../../../components/material-icon/MaterialIcon';
import { EntityCardItemWithIcon } from '../../../entity/components/EntityCardItemWithIcon';
import styles from './lawyer_payment.module.scss';
import { LawyerPaymentFeeBlock } from './components/LawyerPaymentComponents';
import { InjectedIntlProps } from '../../../doctrine.types';
import { LawyerContext } from '../LawyerContext';

type Props = InjectedIntlProps;

export const RawLawyerPayment: FC<Props> = ({ intl }) => {
  const {
    lawyerInfos: {
      lawyerIsVerified,
      paymentMeans,
      spreadPayment,
      firstMeetingFree,
      billingExtraInfo,
      legalAid,
      billing,
    },
  } = useContext(LawyerContext);
  if (!lawyerIsVerified) return null;

  const paymentMeansText = paymentMeansToString(paymentMeans);
  const spreadPaymentText = acceptedChoice(spreadPayment);
  const firstMeetingFreeText = payingChoice(firstMeetingFree);
  const legalAidText = yesNoChoice(legalAid);
  const feesInfo = getFeesInfo(billing);
  const billingInfo = feesInfo ? snakeToCamel(feesInfo) : null;
  const fees = billing.length || billingExtraInfo;

  const hasPaymentInfo =
    paymentMeansText ||
    spreadPaymentText ||
    firstMeetingFreeText ||
    fees ||
    legalAidText;

  if (hasPaymentInfo) {
    window.dispatchEvent(
      new CustomEvent('createNav', {
        detail: 'professional-payment',
      }),
    );
  }

  if (!hasPaymentInfo) return null;

  return (
    <EntityCardContainer id="professional-payment">
      <EntityCardTitle>
        <FormattedMessage id="app.pages.lawyer.payment.title" />
      </EntityCardTitle>
      {paymentMeansText && (
        <EntityCardItemWithIcon
          icon={<EntityCardMaterialIcon name="euro_symbol" />}
          subtitle={<FormattedMessage id="app.pages.lawyer.payment.means" />}
          text={paymentMeansText}
        />
      )}
      {spreadPaymentText && (
        <EntityCardItemWithIcon
          icon={<EntityCardMaterialIcon name="done_all" />}
          subtitle={<FormattedMessage id="app.pages.lawyer.payment.split" />}
          text={spreadPaymentText}
        />
      )}
      {firstMeetingFreeText && (
        <EntityCardItemWithIcon
          icon={<EntityCardMaterialIcon name="filter_1" />}
          subtitle={<FormattedMessage id="app.pages.lawyer.payment.first" />}
          text={firstMeetingFreeText}
        />
      )}
      {legalAidText && (
        <>
          <EntityCardItemWithIcon
            icon={<EntityCardMaterialIcon name="assignment_ind" />}
            subtitle={
              <>
                <FormattedMessage id="app.pages.lawyer.payment.help" />{' '}
                <span
                  data-tip={intl.formatMessage({
                    id: 'lawyer/page/LawyerPayment.tsx/2',
                  })}
                  data-for="payment"
                >
                  <MaterialIcon name="info_outline" className={styles.icon} />
                </span>
              </>
            }
            text={legalAidText}
          />
          <Tooltip id="payment" effect="solid" />
        </>
      )}
      {fees && (
        <EntityCardItemWithIcon
          icon={<EntityCardMaterialIcon name="assignment_turned_in" />}
          subtitle={<FormattedMessage id="app.pages.lawyer.payment.fees" />}
          text={
            <>
              {billingInfo && billingInfo.perHour && (
                <LawyerPaymentFeeBlock
                  title={billingInfo.perHour.title}
                  text={billingInfo.perHour.info}
                />
              )}
              {billingInfo && billingInfo.setPrice && (
                <LawyerPaymentFeeBlock
                  title={billingInfo.setPrice.title}
                  text={billingInfo.setPrice.info}
                />
              )}
              {billingInfo && billingInfo.uponResult && (
                <LawyerPaymentFeeBlock
                  title={billingInfo.uponResult.title}
                  text={billingInfo.uponResult.info}
                />
              )}
              {billingExtraInfo && (
                <div className={billingInfo ? styles.block : ''}>
                  <FormattedMessage id="lawyer/page/LawyerPayment.tsx/1" />
                  <br />
                  <pre>{billingExtraInfo}</pre>
                </div>
              )}
            </>
          }
        />
      )}
    </EntityCardContainer>
  );
};

export const LawyerPayment = injectIntl(RawLawyerPayment);
